import React, { useRef, useState, useEffect } from 'react';
import {
    Autocomplete,
    DrawingManager,
    Marker,
    GoogleMap,
    KmlLayer,
    Polygon,
    Rectangle,
    useJsApiLoader,
    InfoWindow,
} from '@react-google-maps/api';
import currentLocationmarker from "../assets/images/marker.png";
import cityMarkerUser from "../assets/images/CityMarker.png";
import CoordinatesMarker from "../assets/images/coordinatesIcon-marker.png";
import Default_logo from "../assets/images/default_logo.png";
import MapSidebar from './MapSidebar';
import { Modal, Button } from 'react-bootstrap';
import { area } from '@turf/turf';
import { polygon } from '@turf/helpers';
import { featureCollection, point } from '@turf/helpers';
import distance from '@turf/distance';

const baseUrl = process.env.REACT_APP_API_URL;
const libraries = ['places', 'drawing', 'geometry'];
const MAX_AREA_SQ_MILES = 100;
const SQ_KM_TO_SQ_MILES = 0.386102;

// Helper function to convert km to miles
const kmToMiles = (km) => {
    return km * 0.621371;
};

function Map() {
    const mapRef = useRef();
    const polygonRefs = useRef([]);
    const activePolygonIndex = useRef();
    const autocompleteRef = useRef();
    const drawingManagerRef = useRef();
    const kmlLayerRef = useRef(null);

    const [isPortrait, setIsPortrait] = useState(window.innerHeight > window.innerWidth);
    const [selectedMarker, setSelectedMarker] = useState(null);
    const [mobileMarkersListVisible, setMobileMarkersListVisible] = useState(false);
    const [openInfoWindows, setOpenInfoWindows] = useState({});

    useEffect(() => {
        const handleResize = () => {
            setIsPortrait(window.innerHeight > window.innerWidth);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
        libraries,
        version: '3.64' 
    });

    const [polygons, setPolygons] = useState([]);
    const [loading, setLoading] = useState(false);
    const defaultCenter = { lat: 0, lng: 0 };
    const [center, setCenter] = useState(defaultCenter);
    const [CurrentLocationMarkerPosition, setCurrentLocationMarkerPosition] = useState(null);
    const [markerPosition, setMarkerPosition] = useState(null);
    const [isCaptureCursor, setIsCaptureCursor] = useState(false);
    const [hoverCoordinates, setHoverCoordinates] = useState(null);
    const [isClickCaptured, setIsClickCaptured] = useState(false);
    const [isPopupActive, setIsPopupActive] = useState(false);
    const [zoomLevel, setZoomLevel] = useState(2);
    const [kmlUrl, setKmlUrl] = useState(null);
    const [shapeData, setShapeData] = useState(null);
    const [isDisabled, setIsDisabled] = useState(false);
    const [markers, setMarkers] = useState([]);
    const [mapMarkers, setMapMarkers] = useState([]);
    const [loadingMessage, setLoadingMessage] = useState(null);
    const [popupMessage, setPopupMessage] = useState('');
    const [showPopup, setShowPopup] = useState(false);
    const [error, setError] = useState(null);  
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [showAreaPopup, setShowAreaPopup] = useState(false);
    const [areaInfoWindow, setAreaInfoWindow] = useState(null);
    const [areaInfoWindowPosition, setAreaInfoWindowPosition] = useState(null);
    const [areaInSqKm, setAreaInSqKm] = useState(0);
    const [areaInSqMiles, setAreaInSqMiles] = useState(0);
    const [aoiCenter, setAoiCenter] = useState(null);
    const [kmlLoaded, setKmlLoaded] = useState(false);

    // Load AOI center from localStorage on initial mount
    useEffect(() => {
        const savedAoiCenter = localStorage.getItem('aoiCenter');
        if (savedAoiCenter) {
            try {
                setAoiCenter(JSON.parse(savedAoiCenter));
            } catch (e) {
                console.error("Error parsing saved AOI center:", e);
            }
        }
    }, []);

    // Save AOI center to localStorage whenever it changes
    useEffect(() => {
        if (aoiCenter) {
            localStorage.setItem('aoiCenter', JSON.stringify(aoiCenter));
        } else {
            localStorage.removeItem('aoiCenter');
        }
    }, [aoiCenter]);

    // ============ NEW: KML from URL Parameter ============
    useEffect(() => {
        // Get URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const kmlParam = urlParams.get('kml');
        
        if (kmlParam) {
            try {
                console.log('=== KML Received from Django ===');
                console.log('Encoded KML URL:', kmlParam);
                
                // Decode the KML URL
                const decodedKmlUrl = decodeURIComponent(kmlParam);
                console.log('Decoded KML URL:', decodedKmlUrl);
                
                // Set the KML URL
                setKmlUrl(decodedKmlUrl);
                setIsDisabled(false);
                
                // Show toaster
                showToaster('KML file loaded successfully from UAVSphere!', 'success');
                
                // Wait for map to load then load KML
                const checkMapInterval = setInterval(() => {
                    if (mapRef.current && window.google) {
                        clearInterval(checkMapInterval);
                        loadKMLFromUrl(decodedKmlUrl);
                    }
                }, 500);
                
                // Cleanup interval after 10 seconds
                setTimeout(() => clearInterval(checkMapInterval), 10000);
                
            } catch (error) {
                console.error('Error parsing KML from URL:', error);
                showToaster('Error loading KML. Please check console for details.', 'error');
            }
        } else {
            console.log('No KML parameter found in URL');
        }
    }, []);

    // ============ NEW: Load KML from URL ============
    const loadKMLFromUrl = (kmlUrl) => {
        if (!mapRef.current) {
            console.warn('Map not loaded yet');
            return;
        }

        try {
            const google = window.google;
            if (!google) {
                console.error('Google Maps API not loaded');
                return;
            }

            console.log('Loading KML from URL:', kmlUrl);
            
            // Remove existing KML layer if any
            if (kmlLayerRef.current) {
                kmlLayerRef.current.setMap(null);
                kmlLayerRef.current = null;
            }

            // Create new KML layer
            const kmlLayer = new google.maps.KmlLayer({
                url: kmlUrl,
                map: mapRef.current,
                preserveViewport: false,
                suppressInfoWindows: true,
            });

            kmlLayerRef.current = kmlLayer;

            // Handle KML load
            google.maps.event.addListener(kmlLayer, 'status_changed', () => {
                const status = kmlLayer.getStatus();
                console.log('KML Status:', status);
                
                if (status === 'OK') {
                    console.log('✅ KML loaded successfully');
                    setKmlLoaded(true);
                    
                    // Get the KML bounds
                    const bounds = kmlLayer.getDefaultViewport();
                    if (bounds) {
                        mapRef.current.fitBounds(bounds);
                        
                        // Zoom in slightly after fitting
                        google.maps.event.addListenerOnce(mapRef.current, 'idle', () => {
                            const currentZoom = mapRef.current.getZoom();
                            const adjustedZoom = Math.min(currentZoom - 0.5, 18);
                            mapRef.current.setZoom(adjustedZoom);
                        });
                    }
                    
                    showToaster('✅ KML loaded successfully on the map!', 'success');
                } else {
                    console.error('❌ Failed to load KML:', status);
                    showToaster('Failed to load KML file. Please check the URL.', 'error');
                }
            });

            // Handle KML errors
            google.maps.event.addListener(kmlLayer, 'defaultviewport_changed', () => {
                const bounds = kmlLayer.getDefaultViewport();
                if (bounds) {
                    mapRef.current.fitBounds(bounds);
                }
            });

        } catch (error) {
            console.error('Error loading KML:', error);
            showToaster('Error loading KML file. Please check console.', 'error');
        }
    };

    // ============ NEW: Toaster Function ============
    const showToaster = (message, type = 'success') => {
        const existingToaster = document.querySelector('.custom-toaster');
        if (existingToaster) {
            existingToaster.remove();
        }

        const colors = {
            success: '#28a745',
            error: '#dc3545',
            warning: '#ffc107',
            info: '#17a2b8'
        };

        const toaster = document.createElement('div');
        toaster.className = 'custom-toaster';
        toaster.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: ${colors[type] || colors.success};
            color: white;
            padding: 15px 25px;
            border-radius: 8px;
            z-index: 99999;
            font-size: 14px;
            font-weight: 500;
            box-shadow: 0 4px 15px rgba(0,0,0,0.2);
            animation: slideInRight 0.5s ease;
            max-width: 400px;
            display: flex;
            align-items: center;
            gap: 10px;
        `;
        
        const icon = type === 'success' ? '✅' : type === 'error' ? '❌' : type === 'warning' ? '⚠️' : 'ℹ️';
        toaster.innerHTML = `${icon} ${message}`;
        
        document.body.appendChild(toaster);
        
        setTimeout(() => {
            toaster.style.animation = 'slideOutRight 0.5s ease';
            setTimeout(() => toaster.remove(), 500);
        }, 4000);
    };

    // Add CSS animations for toaster
    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            @keyframes slideInRight {
                from {
                    transform: translateX(100%);
                    opacity: 0;
                }
                to {
                    transform: translateX(0);
                    opacity: 1;
                }
            }
            @keyframes slideOutRight {
                from {
                    transform: translateX(0);
                    opacity: 1;
                }
                to {
                    transform: translateX(100%);
                    opacity: 0;
                }
            }
        `;
        document.head.appendChild(style);
        return () => style.remove();
    }, []);

    // ============ END OF NEW CODE ============

    const containerStyle = {
        width: "100vw",
        height: "100vh",
    };

    const mapContainerStyle = {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        margin: 0,
        padding: 0,
        overflow: "hidden",
        zIndex: 0,
    };

    const autocompleteStyle = {
        boxSizing: 'border-box',
        border: '1px solid transparent',
        width: '200px',
        height: '38px',
        padding: '0 12px',
        borderRadius: '3px',
        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
        fontSize: '14px',
        outline: 'none',
        textOverflow: 'ellipses',
        position: 'absolute',
        right: '5%',
        top: '8px',
    };

    const sidebarStyle = {
        width: "0",
        height: "calc(100px - 60px)",
        position: "fixed",
        top: "0",
        left: "0",
        zIndex: 1,
        backgroundColor: "#f8f9fa",
        overflowY: "auto",
    };

    const polygonOptions = {
        strokeColor: "#0000FF",
        strokeOpacity: 1,
        strokeWeight: 2,
        fillColor: "#0000FF",
        fillOpacity: 0.3,
        draggable: false,
        editable: false,
    };

    const rectangleOptions = {
        strokeColor: "#0000FF",
        strokeOpacity: 1,
        strokeWeight: 2,
        fillColor: "#0000FF",
        fillOpacity: 0.3,
        draggable: false,
        editable: false,
    };

    const buttonStyle = {
        position: "absolute",
        bottom: isMobile ? "100px" : "20px",
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 1,
    };

    const drawingManagerOptions = {
        polygonOptions: polygonOptions,
        rectangleOptions: rectangleOptions,
        drawingControl: true,
        drawingControlOptions: {
            position: window.google?.maps?.ControlPosition?.TOP_CENTER,
            drawingModes: [
                window.google?.maps?.drawing?.OverlayType?.POLYGON,
                window.google?.maps?.drawing?.OverlayType?.RECTANGLE,
            ],
        },
    };

    const onLoadMap = (map) => {
        mapRef.current = map;
        if (center) {
            map.setCenter(center);
            map.setZoom(zoomLevel);
        }
    };

    useEffect(() => {
        if (mapRef.current) {
            mapRef.current.setCenter(center);
            mapRef.current.setZoom(zoomLevel);
        }
    }, [center]);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
       
        checkMobile();
        window.addEventListener('resize', checkMobile);
       
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const onLoadPolygon = (polygon, index) => {
        polygonRefs.current[index] = polygon;
    };

    const onClickPolygon = (index) => {
        activePolygonIndex.current = index;
    };

    const onLoadAutocomplete = (autocomplete) => {
        autocompleteRef.current = autocomplete;
    };

    const onPlaceChanged = () => {
        const place = autocompleteRef.current.getPlace();
       
        if (!place || !place.geometry) {
            console.error("Place or geometry is not defined.");
            return;
        }

        const { geometry } = place;
        const bounds = new window.google.maps.LatLngBounds();

        if (geometry.viewport) {
            bounds.union(geometry.viewport);
        } else if (geometry.location) {
            bounds.extend(geometry.location);
        }

        mapRef.current.fitBounds(bounds);
        
        setMarkers([]);
        setMapMarkers([]);
        setOpenInfoWindows({});
        setSelectedMarker(null);
        setMobileMarkersListVisible(false);
        
        if (geometry.location) {
            const center = {
                lat: geometry.location.lat(),
                lng: geometry.location.lng()
            };
            setAoiCenter(center);
        } else if (geometry.viewport) {
            const center = {
                lat: geometry.viewport.getCenter().lat(),
                lng: geometry.viewport.getCenter().lng()
            };
            setAoiCenter(center);
        }
    };

    const onLoadDrawingManager = (drawingManager) => {
        drawingManagerRef.current = drawingManager;
    };

    const calculatePolygonArea = (path) => {
        const google = window.google;
        const areaInSquareMeters = google.maps.geometry.spherical.computeArea(path);
        return (areaInSquareMeters / 1e6) * SQ_KM_TO_SQ_MILES;
    };

    const calculateAOICenter = (shape) => {
        if (!shape) return null;
       
        try {
            let coordinates = [];
            if (shape.type === "rectangle") {
                coordinates = shape.bounds;
            } else if (shape.type === "polygon") {
                coordinates = shape.paths;
            }

            if (coordinates.length === 0) return null;

            let lat = 0, lng = 0;
            coordinates.forEach(coord => {
                lat += coord.lat;
                lng += coord.lng;
            });
           
            return {
                lat: lat / coordinates.length,
                lng: lng / coordinates.length
            };
        } catch (error) {
            console.error("Error calculating AOI center:", error);
            return null;
        }
    };

    const calculateDistance = (point1, point2) => {
        if (!point1 || !point2) {
            return null;
        }
       
        if (typeof point1.lat !== 'number' || typeof point1.lng !== 'number' ||
            typeof point2.lat !== 'number' || typeof point2.lng !== 'number') {
            return null;
        }

        try {
            const from = point([point1.lng, point1.lat]);
            const to = point([point2.lng, point2.lat]);
            return distance(from, to, { units: 'kilometers' });
        } catch (error) {
            console.error("Error calculating distance:", error);
            return null;
        }
    };

    const formatDistance = (distanceInKm) => {
        if (distanceInKm === null || distanceInKm === undefined) {
            return 'Distance unavailable';
        }
       
        if (distanceInKm === Infinity) {
            return 'Distance unavailable';
        }
       
        if (typeof distanceInKm !== 'number' || isNaN(distanceInKm)) {
            return 'Distance unavailable';
        }
       
        const distanceInMiles = kmToMiles(distanceInKm);
       
        if (distanceInMiles < 0.1) {
            const feet = Math.round(distanceInMiles * 5280);
            return `${feet} ft away`;
        } else if (distanceInMiles < 1) {
            return `${distanceInMiles.toFixed(2)} miles away`;
        } else {
            return `${distanceInMiles.toFixed(1)} miles away`;
        }
    };

    const onOverlayComplete = ($overlayEvent) => {
        setMarkers([]);
        setMapMarkers([]);
        setOpenInfoWindows({});
        setSelectedMarker(null);
        setMobileMarkersListVisible(false);
        
        if (areaInfoWindow) {
            areaInfoWindow.close();
            setAreaInfoWindow(null);
            setAreaInfoWindowPosition(null);
        }
        
        // Clear KML when drawing new shape
        if (kmlLayerRef.current) {
            kmlLayerRef.current.setMap(null);
            kmlLayerRef.current = null;
        }
        setKmlUrl(null);
        setKmlLoaded(false);

        const filtered = polygons.filter(
            (polygon, index) => index !== activePolygonIndex.current
        );
        setPolygons(filtered);

        let newShape;
        let area = 0;
        let areaInSqKm = 0;
        let areaInSqMiles = 0;
        let centerLat = 0;
        let centerLng = 0;

        if ($overlayEvent.type === window.google.maps.drawing.OverlayType.POLYGON) {
            const path = $overlayEvent.overlay.getPath().getArray();
            newShape = {
                type: "polygon",
                paths: path.map((latLng) => ({ lat: latLng.lat(), lng: latLng.lng() })),
            };

            const startPoint = newShape.paths[0];
            newShape.paths.push(startPoint);
            
            const areaInSqMeters = window.google.maps.geometry.spherical.computeArea(path);
            areaInSqKm = areaInSqMeters / 1e6;
            areaInSqMiles = areaInSqKm * SQ_KM_TO_SQ_MILES;
            area = areaInSqMiles;
            
            let sumLat = 0;
            let sumLng = 0;
            path.forEach(point => {
                sumLat += point.lat();
                sumLng += point.lng();
            });
            centerLat = sumLat / path.length;
            centerLng = sumLng / path.length;
            setAoiCenter({ lat: centerLat, lng: centerLng });
            
        } else if ($overlayEvent.type === window.google.maps.drawing.OverlayType.RECTANGLE) {
            const bounds = $overlayEvent.overlay.getBounds();
            const ne = bounds.getNorthEast();
            const sw = bounds.getSouthWest();

            newShape = {
                type: "rectangle",
                bounds: [
                    { lat: sw.lat(), lng: sw.lng() },
                    { lat: ne.lat(), lng: sw.lng() },
                    { lat: ne.lat(), lng: ne.lng() },
                    { lat: sw.lat(), lng: ne.lng() },
                ],
            };

            const path = [
                new window.google.maps.LatLng(sw.lat(), sw.lng()),
                new window.google.maps.LatLng(ne.lat(), sw.lng()),
                new window.google.maps.LatLng(ne.lat(), ne.lng()),
                new window.google.maps.LatLng(sw.lat(), ne.lng())
            ];

            const areaInSqMeters = window.google.maps.geometry.spherical.computeArea(path);
            areaInSqKm = areaInSqMeters / 1e6;
            areaInSqMiles = areaInSqKm * SQ_KM_TO_SQ_MILES;
            area = areaInSqMiles;
            
            centerLat = (ne.lat() + sw.lat()) / 2;
            centerLng = (ne.lng() + sw.lng()) / 2;
            setAoiCenter({ lat: centerLat, lng: centerLng });
        }

        if (area > MAX_AREA_SQ_MILES) {
            $overlayEvent.overlay.setMap(null);
            setError(`The area exceeds the maximum allowed size of ${MAX_AREA_SQ_MILES} square miles.`);
            setShowErrorModal(true);
            return;
        }

        const formattedAreaSqKm = areaInSqKm.toFixed(2);
        const formattedAreaSqMiles = areaInSqMiles.toFixed(2);
        
        setAreaInfoWindowPosition({ lat: centerLat, lng: centerLng });
        
        if (mapRef.current && centerLat && centerLng) {
            const infoWindow = new window.google.maps.InfoWindow({
                content: `
                    <div style="padding: 12px 16px; min-width: 140px;">
                        <strong style="font-size: 14px;">Area of Interest</strong>
                        <p style="margin: 5px 0; font-size: 13px;">
                            <strong>${formattedAreaSqKm} sq km</strong>
                        </p>
                    </div>
                `,
                position: { lat: centerLat, lng: centerLng },
                pixelOffset: new window.google.maps.Size(0, -20)
            });
            
            infoWindow.open(mapRef.current);
            setAreaInfoWindow(infoWindow);
            
            window.google.maps.event.addListener(infoWindow, 'closeclick', () => {
                setAreaInfoWindow(null);
                setAreaInfoWindowPosition(null);
            });
        }

        $overlayEvent.overlay?.setMap(null);
        setPolygons([newShape]);

        const newPolygonDataItems =
            newShape.type === "rectangle"
                ? newShape.bounds.map((cord) => [cord.lng, cord.lat])
                : newShape.paths.map((cord) => [cord.lng, cord.lat]);

        if (!newPolygonDataItems) {
            console.error("Invalid polygon data", newShape);
            return;
        }

        const postData = {
            type: "Polygon",
            coordinates: [newPolygonDataItems],
            newPolygon: newShape,
        };

        setShapeData(postData);
        drawingManagerRef.current.setDrawingMode(null);
    };

    const handleCloseModal = () => {
        setShowErrorModal(false);
        setLoading(false);
    };

    const generateKml = async () => {
        setLoadingMessage("Searching for drone pilots and companies in and around your area of interest.");
        setLoading(true);
        try {
            let postData;
            if (kmlUrl) {
                postData = { kml_url: kmlUrl };
            } else if (shapeData) {
                postData = shapeData;
            } else {
                console.error("No KML URL or shape data available.");
                return;
            }

            const response = await fetch(`${baseUrl}/generate_customer_kml`, {
                method: "POST",
                body: JSON.stringify(postData),
                headers: {
                    "Content-Type": "application/json; charset=UTF-8",
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const json = await response.json();
            console.log("API Response:", json);

            if (json.intersection_data && json.intersection_data.users && json.intersection_data.users.length > 0) {
                await handleMultipleUsers(json.intersection_data.users);
            } else {
                setPopupMessage("No drone operators or companies have been found in your area of interest. Would you like us to search for one?");
                setShowPopup(true);
            }

            if (json.response) {
                localStorage.setItem("kmlResponseUrl", json.response);
            }

        } catch (error) {
            console.error("Error posting data:", error);
        } finally {
            setLoading(false);
            setLoadingMessage(null);
        }
    };

    const handleMultipleUsers = async (users) => {
        const updatedMarkers = [];
        const usersWithDistances = [];

        for (const user of users) {
            const {
                _id,
                city,
                country,
                ZIPCode,
                company_logo,
                email,
                drones_do_you_have,
                services,
                state,
                geometryfile,
                GeoWGS84_partner_no,
                firstname,
                lastname,
                first_name,
                last_name,
                phonenumber,
                address,
                company,
                companydetails,
                companywebsite
            } = user;

            if (city && country) {
                const markerData = await geocodeCity(city, country);
                if (markerData) {
                    const userData = {
                        ...markerData,
                        _id: _id || GeoWGS84_partner_no,
                        city,
                        country,
                        ZIPCode,
                        company_logo,
                        email,
                        drones_do_you_have,
                        services,
                        state,
                        geometryfile,
                        GeoWGS84_partner_no,
                        firstname: firstname || first_name,
                        lastname: lastname || last_name,
                        phonenumber,
                        address,
                        company,
                        companydetails,
                        companywebsite
                    };
                   
                    updatedMarkers.push(userData);
                   
                    if (aoiCenter) {
                        const dist = calculateDistance(aoiCenter, markerData.position);
                        usersWithDistances.push({
                            ...userData,
                            distance: dist
                        });
                    } else {
                        usersWithDistances.push({
                            ...userData,
                            distance: null
                        });
                    }
                }
            } else {
                console.warn("User information is incomplete.");
            }
        }

        const sortedMarkers = usersWithDistances.sort((a, b) => {
            if (a.distance === null && b.distance === null) return 0;
            if (a.distance === null) return 1;
            if (b.distance === null) return -1;
            return a.distance - b.distance;
        });
       
        setMarkers(sortedMarkers);

        const nearestMarkers = sortedMarkers
            .filter(m => m.distance !== null)
            .slice(0, 5);
           
        if (nearestMarkers.length < 5 && sortedMarkers.length > nearestMarkers.length) {
            const unknownDistanceMarkers = sortedMarkers.filter(m => m.distance === null);
            while (nearestMarkers.length < 5 && unknownDistanceMarkers.length > 0) {
                nearestMarkers.push(unknownDistanceMarkers.shift());
            }
        }
       
        setMapMarkers(nearestMarkers);

        if (!isPortrait && nearestMarkers.length > 0) {
            const openWindows = {};
            nearestMarkers.forEach((_, index) => {
                openWindows[index] = true;
            });
            setOpenInfoWindows(openWindows);
        }

        if (sortedMarkers.length > 0) {
            const bounds = new window.google.maps.LatLngBounds();

            sortedMarkers.forEach(marker => {
                if (marker.position) {
                    bounds.extend(marker.position);
                }
            });

            if (aoiCenter) {
                bounds.extend(aoiCenter);
            }

            if (mapRef.current && !bounds.isEmpty()) {
                mapRef.current.fitBounds(bounds);
            }
        }
    };

    const geocodeCity = async (city, country) => {
        const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
        const geocodeUrl = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(city)},${encodeURIComponent(country)}&key=${apiKey}`;

        try {
            const response = await fetch(geocodeUrl);
            const data = await response.json();

            if (data.results.length > 0) {
                const location = data.results[0].geometry.location;
                const buffer = Math.random() * (0.0009 - 0.0005) + 0.0005;

                return {
                    position: { lat: location.lat + buffer, lng: location.lng + buffer },
                    city,
                    country,
                };
            } else {
                setPopupMessage("No user found");
                setShowPopup(true);
                return null;
            }
        } catch (error) {
            console.error("Error geocoding the city:", error);
            return null;
        }
    };

    const handleDetailClick = (user) => {
        const selectedUser = {
            _id: user._id || user.GeoWGS84_partner_no,
            firstname: user.firstname || user.first_name,
            lastname: user.lastname || user.last_name,
            email: user.email,
            phonenumber: user.phonenumber,
            company: user.company,
            company_logo: user.company_logo,
            companydetails: user.companydetails,
            companywebsite: user.companywebsite,
            GeoWGS84_partner_no: user.GeoWGS84_partner_no,
            address: user.address,
            city: user.city,
            state: user.state,
            country: user.country,
            ZIPCode: user.ZIPCode,
            services: user.services,
            drones_do_you_have: user.drones_do_you_have,
            geometryfile: user.geometryfile,
            distance: user.distance
        };

        console.log("Storing user in localStorage:", selectedUser);
        localStorage.setItem('selectedUser', JSON.stringify(selectedUser));
        window.open(`/ProviderDetails`, '_blank');
    };

    const onEditPolygon = (index) => {
        // Edit polygon functionality
    };

    const handleGenerateKml = () => {
        if (shapeData || kmlUrl) {
            generateKml(shapeData, kmlUrl);
        } else {
            console.error('No shape data or KML URL available to generate KML.');
        }
    };

    const buttonLabel = kmlUrl || shapeData
        ? "Search for drone pilots and companies in and around your area of interest"
        : "Please draw your area of interest";

    const onKMLUpload = async (file) => {
        if (areaInfoWindow) {
            areaInfoWindow.close();
            setAreaInfoWindow(null);
            setAreaInfoWindowPosition(null);
        }
        setLoading(true);
        
        setMarkers([]);
        setMapMarkers([]);
        setOpenInfoWindows({});
        setSelectedMarker(null);
        setMobileMarkersListVisible(false);

        const reader = new FileReader();
        reader.onload = async (e) => {
            const kmlContent = e.target.result;
            const polygonCoords = parseKML(kmlContent);

            let totalArea = 0;
            polygonCoords.forEach(coords => {
                totalArea += calculateKMLUploadPolygonArea(coords);
            });

            if (totalArea > 100) {
                setError('The uploaded area exceeds the maximum limit of 100 square miles.');
                setShowErrorModal(true);
                setLoading(false);
                return;
            }

            if (polygonCoords.length > 0) {
                const coords = polygonCoords[0];
                let lat = 0, lng = 0;
                coords.forEach(coord => {
                    lat += coord.lat;
                    lng += coord.lng;
                });
                const center = {
                    lat: lat / coords.length,
                    lng: lng / coords.length
                };
                setAoiCenter(center);
            }

            const formData = new FormData();
            formData.append('kml_file', file);
            formData.append('user_type', 'customer');

            try {
                const response = await fetch(`${baseUrl}/upload_kml_on_map`, {
                    method: 'POST',
                    body: formData
                });

                if (response.ok) {
                    const data = await response.json();
                    setKmlUrl(data.url);
                    setIsDisabled(false);
                    // Load the KML after upload
                    if (mapRef.current) {
                        loadKMLFromUrl(data.url);
                    }
                } else {
                    console.error('Failed to upload KML file');
                }
            } catch (error) {
                console.error('Error uploading KML file:', error);
            } finally {
                setLoading(false);
            }
        };

        reader.readAsText(file);
    };

    const parseKML = (kmlContent) => {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(kmlContent, "application/xml");
        const coordinates = xmlDoc.getElementsByTagName("coordinates");

        const polygonCoords = [];
        for (let i = 0; i < coordinates.length; i++) {
            const coordStr = coordinates[i].textContent.trim();
            const coordPairs = coordStr.split(/\s+/);
            const latLngs = coordPairs.map((pair) => {
                const [lng, lat] = pair.split(',').map(Number);
                return { lat, lng };
            });
            polygonCoords.push(latLngs);
        }
        return polygonCoords;
    };

    const calculateKMLUploadPolygonArea = (polygonCoords) => {
        const poly = polygon([polygonCoords.map(coord => [coord.lng, coord.lat])]);
        const areaInSqKm = area(poly) / 1e6;
        const areaInSqMiles = areaInSqKm * 0.386102;
        return areaInSqMiles;
    };

    const handleMouseMove = (e) => {
        if (isCaptureCursor && e.latLng) {
            const latLng = e.latLng.toJSON();
            setHoverCoordinates(latLng);
        }
    };

    const handleMapClick = () => {
        if (isCaptureCursor && !isClickCaptured) {
            setIsClickCaptured(true);
            setHoverCoordinates(null);
        }
    };

    const handleCloseInfoWindow = () => {
        setIsCaptureCursor(false);
        setHoverCoordinates(null);
    };

    const handleCursorCapture = () => {
        setIsCaptureCursor((prev) => !prev);
        setIsClickCaptured(false);
        setIsPopupActive(!isPopupActive);
        if (isCaptureCursor) {
            setHoverCoordinates(null);
        }
    };

    const handleKmlLayerLoad = () => {
        if (kmlUrl && !kmlLoaded) {
            loadKMLFromUrl(kmlUrl);
        }
    };

    useEffect(() => {
        handleKmlLayerLoad();
    }, [kmlUrl]);

    const zoomToDrawnShapes = () => {
        if (mapRef.current && kmlUrl && kmlLoaded) {
            // Zoom to KML bounds
            if (kmlLayerRef.current) {
                const bounds = kmlLayerRef.current.getDefaultViewport();
                if (bounds) {
                    mapRef.current.fitBounds(bounds);
                    window.google.maps.event.addListenerOnce(mapRef.current, 'idle', () => {
                        const boundsCenter = bounds.getCenter();
                        const currentZoom = mapRef.current.getZoom();
                        const adjustedZoom = currentZoom - 1;
                        mapRef.current.setCenter(boundsCenter);
                        mapRef.current.setZoom(adjustedZoom);
                    });
                }
            }
        }
        else if (mapRef.current && polygons.length > 0) {
            const bounds = new window.google.maps.LatLngBounds();
           
            polygons.forEach(shape => {
                if (shape.type === "rectangle") {
                    shape.bounds.forEach(point => {
                        bounds.extend(point);
                    });
                } else if (shape.type === "polygon") {
                    shape.paths.forEach(point => {
                        bounds.extend(point);
                    });
                }
            });
           
            mapRef.current.fitBounds(bounds);
           
            window.google.maps.event.addListenerOnce(mapRef.current, 'idle', () => {
                const boundsCenter = bounds.getCenter();
                const currentZoom = mapRef.current.getZoom();
                const adjustedZoom = currentZoom - 1;
                mapRef.current.setCenter(boundsCenter);
                mapRef.current.setZoom(adjustedZoom);
            });
        }
        else if (mapRef.current && aoiCenter) {
            mapRef.current.setCenter(aoiCenter);
            mapRef.current.setZoom(12);
        }
        else {
            console.log("No AOI (KML file or drawn shape) found to zoom to.");
        }
    };

    const CountrySelectKmlUrl = async (url) => {
        if (areaInfoWindow) {
            areaInfoWindow.close();
            setAreaInfoWindow(null);
            setAreaInfoWindowPosition(null);
        }
        setPolygons([]);
        setKmlUrl(url);
        setIsDisabled(false);
        setKmlLoaded(false);
        
        setMarkers([]);
        setMapMarkers([]);
        setOpenInfoWindows({});
        setSelectedMarker(null);
        setMobileMarkersListVisible(false);
        
        // Load the KML
        if (mapRef.current) {
            loadKMLFromUrl(url);
        }
        
        try {
            const response = await fetch(url);
            const kmlText = await response.text();
            const polygonCoords = parseKML(kmlText);
            
            if (polygonCoords.length > 0) {
                const coords = polygonCoords[0];
                let lat = 0, lng = 0;
                coords.forEach(coord => {
                    lat += coord.lat;
                    lng += coord.lng;
                });
                const center = {
                    lat: lat / coords.length,
                    lng: lng / coords.length
                };
                setAoiCenter(center);
            }
        } catch (error) {
            console.error("Error calculating center from KML:", error);
        }
    };

    useEffect(() => {
        if (kmlUrl || shapeData) {
            setIsDisabled(false);
        } else {
            setIsDisabled(true);
        }
    }, [kmlUrl, shapeData]);

    const handleInfoWindowToggle = (index) => {
        if (!isPortrait) {
            setOpenInfoWindows(prev => ({
                ...prev,
                [index]: !prev[index]
            }));
        }
    };

    const handleCloseInfoWindowAtIndex = (index) => {
        setOpenInfoWindows(prev => ({
            ...prev,
            [index]: false
        }));
    };

    return (
        isLoaded ? (
            <div style={{ display: "flex" }}>
                {!isPortrait && (
                    <div style={sidebarStyle}>
                        <MapSidebar
                            onKMLUpload={onKMLUpload}
                            setKmlUrl={CountrySelectKmlUrl}
                            setCurrentLocationMarkerPosition={setCurrentLocationMarkerPosition}
                            setMarkerPosition={setMarkerPosition}  
                            setCaptureCoordinates={handleCursorCapture}
                            setCenter={setCenter}
                            setZoomLevel={setZoomLevel}
                            zoomToDrawnShapes={zoomToDrawnShapes}
                        />
                    </div>
                )}
               
                {isPortrait && (
                    <div style={{
                        position: 'absolute',
                        top: '10px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 1000,
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        padding: '8px',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.3)',
                    }}>
                        <label htmlFor="countrySelect">Select a country: </label>
                        <select
                            id="countrySelect"
                            onChange={(e) => {
                                const country = e.target.value;
                                if (country) {
                                    const formatted = country.replace(/\s+/g, '%20');
                                    const kmlUrl = `https://kmlgenerationgeowgs84.blob.core.windows.net/world-country-area-kml-files/${formatted}.kml`;
                                    CountrySelectKmlUrl(kmlUrl);
                                }
                            }}
                        >
                            <option value="">-- Choose a country --</option>
                            {Object.entries(require('../assets/countriesJSON/countries.json')).map(
                                ([key, value], index) => (
                                    <option key={index} value={key}>{value}</option>
                                )
                            )}
                        </select>
                    </div>
                )}
               
                <div className="map-container" style={mapContainerStyle}>
                    {loading && (
                        <div style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            padding: '10px',
                            borderRadius: '5px',
                            zIndex: 1000,
                            textAlign: 'center'
                        }}>
                            <div className="loading-container">
                                <div className="spinner-border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p style={{ marginTop: '10px' }}>{loadingMessage || "Loading..."}</p>
                            </div>
                        </div>
                    )}

                    <GoogleMap
                        zoom={zoomLevel}
                        center={center}
                        onLoad={onLoadMap}
                        mapContainerStyle={containerStyle}
                        onTilesLoaded={() => setCenter(null)}
                        onMouseMove={handleMouseMove}
                        onClick={handleMapClick}
                        options={{
                            fullscreenControl: false,
                            streetViewControl: false,
                            mapTypeControl: !isPortrait,
                            zoomControl: false,
                        }}
                    >
                        {!isPortrait && (
                            <DrawingManager
                                onLoad={onLoadDrawingManager}
                                onOverlayComplete={onOverlayComplete}
                                options={drawingManagerOptions}
                            />
                        )}

                        {polygons.map((shape, index) => {
                            if (shape.type === "rectangle") {
                                return (
                                    <Rectangle
                                        key={index}
                                        onLoad={(event) => onLoadPolygon(event, index)}
                                        onMouseDown={() => onClickPolygon(index)}
                                        onMouseUp={() => onEditPolygon(index)}
                                        onDragEnd={() => onEditPolygon(index)}
                                        options={rectangleOptions}
                                        bounds={
                                            new window.google.maps.LatLngBounds(
                                                shape.bounds[0],
                                                shape.bounds[2]
                                            )
                                        }
                                        draggable
                                        editable
                                    />
                                );
                            } else {
                                return (
                                    <Polygon
                                        key={index}
                                        onLoad={(event) => onLoadPolygon(event, index)}
                                        onMouseDown={() => onClickPolygon(index)}
                                        onMouseUp={() => onEditPolygon(index)}
                                        onDragEnd={() => onEditPolygon(index)}
                                        options={polygonOptions}
                                        paths={shape.paths}
                                        draggable
                                        editable
                                    />
                                );
                            }
                        })}
                       
                        {!isPortrait && (
                            <Autocomplete
                                onLoad={onLoadAutocomplete}
                                onPlaceChanged={onPlaceChanged}
                            >
                                <input
                                    type='text'
                                    placeholder='Search Location'
                                    style={autocompleteStyle}
                                />
                            </Autocomplete>
                        )}
                       
                        {markerPosition && (
                            <Marker
                                position={markerPosition}
                                icon={CoordinatesMarker}
                            />
                        )}
                       
                        {CurrentLocationMarkerPosition && (
                            <Marker
                                position={CurrentLocationMarkerPosition}
                                icon={currentLocationmarker}
                            />
                        )}

                        {/* Markers with InfoWindows - Only show nearest 5 */}
                        {mapMarkers.length > 0 && mapMarkers.map((marker, index) => (
                            <React.Fragment key={index}>
                                <Marker
                                    position={marker.position}
                                    icon={cityMarkerUser}
                                    onClick={() => {
                                        if (isPortrait) {
                                            setSelectedMarker(marker);
                                        } else {
                                            handleInfoWindowToggle(index);
                                        }
                                    }}
                                />
                               
                                {!isPortrait && openInfoWindows[index] && (
                                    <InfoWindow
                                        position={marker.position}
                                        onCloseClick={() => handleCloseInfoWindowAtIndex(index)}
                                        options={{
                                            pixelOffset: new window.google.maps.Size(0, -50),
                                        }}
                                    >
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                textAlign: 'center',
                                                minWidth: '200px',
                                                maxWidth: '220px',
                                                padding: '8px'
                                            }}
                                        >
                                            <img
                                                src={marker.company_logo
                                                    ? `data:image/png;base64,${marker.company_logo}`
                                                    : Default_logo}
                                                alt="Logo"
                                                style={{
                                                    width: "40px",
                                                    height: "40px",
                                                    objectFit: "contain",
                                                    marginBottom: "10px"
                                                }}
                                            />
                                            {marker.company && (
                                                <p style={{ margin: 0, fontSize: "13px", fontWeight: "bold" }}>
                                                    {marker.company}
                                                </p>
                                            )}
                                            <p style={{ margin: 0, fontSize: "12px" }}>
                                                Partner No: {marker.GeoWGS84_partner_no}
                                            </p>
                                            <p style={{ margin: 0, fontSize: "12px" }}>{marker.city}</p>
                                            <p style={{ marginBottom: "5px", fontSize: "12px" }}>{marker.country}</p>
                                            <p style={{ margin: "0 0 10px 0", fontSize: "11px", color: "#666", fontStyle: "italic" }}>
                                                {formatDistance(marker.distance)}
                                            </p>
                                            <button
                                                onClick={() => handleDetailClick(marker)}
                                                style={{
                                                    backgroundColor: '#EC9712',
                                                    color: '#fff',
                                                    border: 'none',
                                                    padding: '6px 12px',
                                                    borderRadius: '5px',
                                                    fontSize: '13px',
                                                    cursor: 'pointer',
                                                    width: '100%'
                                                }}
                                            >
                                                Details
                                            </button>
                                        </div>
                                    </InfoWindow>
                                )}
                            </React.Fragment>
                        ))}

                        {isPortrait && markers.length > 0 && (
                            <div style={{
                               position: "absolute",
                                bottom: "290px",
                                left: "50%",
                                transform: "translateX(-50%)",
                                zIndex: 1,
                            }}>
                                <Button
                                    onClick={() => setMobileMarkersListVisible(!mobileMarkersListVisible)}
                                    style={{
                                        backgroundColor: '#28a745',
                                        borderColor: '#28a745',
                                        fontSize: '14px',
                                        padding: '8px 16px',
                                        boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {mobileMarkersListVisible ? 'Hide Providers' : `Show ${markers.length} Providers`}
                                </Button>
                            </div>
                        )}

                        {isPopupActive && hoverCoordinates && (
                            <InfoWindow
                                position={hoverCoordinates}
                                onCloseClick={handleCloseInfoWindow}
                            >
                                <div>
                                    <p>Latitude: {hoverCoordinates.lat.toFixed(6)}</p>
                                    <p>Longitude: {hoverCoordinates.lng.toFixed(6)}</p>
                                </div>
                            </InfoWindow>
                        )}
                       
                        {/* KML Layer - This renders the KML from URL */}
                        {kmlUrl && (
                            <KmlLayer
                                url={kmlUrl}
                                options={{ preserveViewport: false }}
                                onLoad={(kml) => {
                                    console.log('KML Layer loaded');
                                    setKmlLoaded(true);
                                }}
                                onError={(error) => {
                                    console.error('KML Load Error:', error);
                                    showToaster('Error loading KML file.', 'error');
                                }}
                            />
                        )}
                    </GoogleMap>

                    {!isPortrait && markers.length > 0 && (
                        <div style={{
                            position: 'fixed',
                            bottom: '100px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            zIndex: 1500,
                        }}>
                            <Button
                                onClick={() => setMobileMarkersListVisible(!mobileMarkersListVisible)}
                                style={{
                                    backgroundColor: mobileMarkersListVisible ? '#dc3545' : '#28a745',
                                    borderColor: mobileMarkersListVisible ? '#dc3545' : '#28a745',
                                    fontSize: '14px',
                                    padding: '10px 20px',
                                    boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
                                    whiteSpace: 'nowrap',
                                    fontWeight: '500',
                                    transition: 'all 0.3s ease'
                                }}
                            >
                                {mobileMarkersListVisible ? 'Hide Providers List' : `Show ${markers.length} Providers`}
                            </Button>
                        </div>
                    )}

                    {!isPortrait && markers.length > 0 && mobileMarkersListVisible && (
                        <div style={{
                            position: 'fixed',
                            top: 0,
                            bottom: 0,
                            left: 0,
                            width: '400px',
                            backgroundColor: 'white',
                            boxShadow: '2px 0 10px rgba(0,0,0,0.2)',
                            zIndex: 2000,
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            animation: 'slideIn 0.3s ease'
                        }}>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '15px 20px',
                                borderBottom: '1px solid #eee',
                                backgroundColor: '#f8f9fa'
                            }}>
                                <h5 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                                    Found {markers.length} Providers (Sorted by Distance)
                                </h5>
                                <button
                                    onClick={() => setMobileMarkersListVisible(false)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        fontSize: '24px',
                                        cursor: 'pointer',
                                        color: '#666',
                                        width: '40px',
                                        height: '40px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '50%',
                                        transition: 'background-color 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                >
                                    ✕
                                </button>
                            </div>
                           
                            <div style={{
                                flex: 1,
                                overflowY: 'auto',
                                padding: '15px'
                            }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    {markers.map((marker, index) => (
                                        <div
                                            key={index}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px',
                                                padding: '12px',
                                                borderRadius: '8px',
                                                border: '1px solid #e9ecef',
                                                backgroundColor: selectedMarker === marker ? '#f8f9fa' : 'white',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s',
                                                boxShadow: selectedMarker === marker ? '0 2px 8px rgba(0,0,0,0.1)' : 'none'
                                            }}
                                            onClick={() => setSelectedMarker(marker)}
                                            onMouseEnter={(e) => {
                                                if (selectedMarker !== marker) {
                                                    e.currentTarget.style.backgroundColor = '#f8f9fa';
                                                }
                                            }}
                                            onMouseLeave={(e) => {
                                                if (selectedMarker !== marker) {
                                                    e.currentTarget.style.backgroundColor = 'white';
                                                }
                                            }}
                                        >
                                            <img
                                                src={marker.company_logo
                                                    ? `data:image/png;base64,${marker.company_logo}`
                                                    : Default_logo}
                                                alt="Logo"
                                                style={{
                                                    width: "45px",
                                                    height: "45px",
                                                    objectFit: "contain",
                                                    borderRadius: '4px'
                                                }}
                                            />
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                {marker.company && (
                                                    <p style={{
                                                        margin: 0,
                                                        fontSize: "14px",
                                                        fontWeight: "bold",
                                                        whiteSpace: 'nowrap',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis'
                                                    }}>
                                                        {marker.company}
                                                    </p>
                                                )}
                                                <p style={{
                                                    margin: '4px 0 0',
                                                    fontSize: "12px",
                                                    color: "#666",
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}>
                                                    {marker.city}, {marker.country}
                                                </p>
                                                <p style={{
                                                    margin: '2px 0 0',
                                                    fontSize: "11px",
                                                    color: "#999",
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis'
                                                }}>
                                                    Partner No: {marker.GeoWGS84_partner_no}
                                                </p>
                                                <p style={{
                                                    margin: '2px 0 0',
                                                    fontSize: '10px',
                                                    color: marker.distance !== null && marker.distance !== Infinity ? '#28a745' : '#999',
                                                    fontWeight: '500'
                                                }}>
                                                    {formatDistance(marker.distance)}
                                                </p>
                                            </div>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDetailClick(marker);
                                                }}
                                                style={{
                                                    backgroundColor: '#EC9712',
                                                    color: '#fff',
                                                    border: 'none',
                                                    padding: '6px 12px',
                                                    borderRadius: '5px',
                                                    fontSize: '12px',
                                                    cursor: 'pointer',
                                                    whiteSpace: 'nowrap',
                                                    transition: 'background-color 0.2s'
                                                }}
                                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#da8a10'}
                                                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#EC9712'}
                                            >
                                                Details
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {isPortrait && markers.length > 0 && mobileMarkersListVisible && (
                        <div style={{
                            position: 'fixed',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            borderTopLeftRadius: '15px',
                            borderTopRightRadius: '15px',
                            boxShadow: '0 -2px 10px rgba(0,0,0,0.2)',
                            zIndex: 1000,
                            maxHeight: '50vh',
                            overflowY: 'auto',
                            padding: '15px'
                        }}>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '15px',
                                paddingBottom: '10px',
                                borderBottom: '1px solid #eee'
                            }}>
                                <h5 style={{ margin: 0 }}>Found {markers.length} Providers (Sorted by Distance)</h5>
                                <button
                                    onClick={() => setMobileMarkersListVisible(false)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        fontSize: '20px',
                                        cursor: 'pointer',
                                        color: '#666'
                                    }}
                                >
                                    ✕
                                </button>
                            </div>
                           
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {markers.map((marker, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            padding: '10px',
                                            borderRadius: '8px',
                                            border: '1px solid #eee',
                                            backgroundColor: selectedMarker === marker ? '#f8f9fa' : 'white'
                                        }}
                                        onClick={() => setSelectedMarker(marker)}
                                    >
                                        <img
                                            src={marker.company_logo
                                                ? `data:image/png;base64,${marker.company_logo}`
                                                : Default_logo}
                                            alt="Logo"
                                            style={{
                                                width: "40px",
                                                height: "40px",
                                                objectFit: "contain"
                                            }}
                                        />
                                        <div style={{ flex: 1 }}>
                                            {marker.company && (
                                                <p style={{ margin: 0, fontSize: "14px", fontWeight: "bold" }}>
                                                    {marker.company}
                                                </p>
                                            )}
                                            <p style={{ margin: 0, fontSize: "12px", color: "#666" }}>
                                                {marker.city}, {marker.country}
                                            </p>
                                            <p style={{ margin: 0, fontSize: "12px", color: "#666" }}>
                                                Partner No: {marker.GeoWGS84_partner_no}
                                            </p>
                                            <p style={{
                                                margin: 0,
                                                fontSize: '11px',
                                                color: marker.distance !== null && marker.distance !== Infinity ? '#28a745' : '#999'
                                            }}>
                                                {formatDistance(marker.distance)}
                                            </p>
                                        </div>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDetailClick(marker);
                                            }}
                                            style={{
                                                backgroundColor: '#EC9712',
                                                color: '#fff',
                                                border: 'none',
                                                padding: '6px 12px',
                                                borderRadius: '5px',
                                                fontSize: '12px',
                                                cursor: 'pointer',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            Details
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <style>
                        {`
                            @keyframes slideIn {
                                from {
                                    transform: translateX(-100%);
                                }
                                to {
                                    transform: translateX(0);
                                }
                            }
                        `}
                    </style>

                    <Modal show={showErrorModal} onHide={handleCloseModal}>
                        <Modal.Header closeButton>
                            <Modal.Title>Alert</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>{error}</Modal.Body>
                        <Modal.Footer>
                            <Button
                                variant="secondary"
                                onClick={handleCloseModal}
                                style={{ backgroundColor: '#EC9712', borderColor: '#EC9712' }}
                            >
                                Close
                            </Button>
                        </Modal.Footer>
                    </Modal>
                   
                    <Button
                        onClick={handleGenerateKml}
                        disabled={!kmlUrl && !shapeData}
                        style={buttonStyle}
                    >
                        {buttonLabel}
                    </Button>
                </div>
               
                {showPopup && (
                    <div style={{
                        position: 'fixed',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        backgroundColor: '#fff',
                        border: '1px solid #ccc',
                        borderRadius: '8px',
                        padding: '30px',
                        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
                        zIndex: 1000,
                        width: '400px',
                        maxWidth: '90%',
                        textAlign: 'center'
                    }}>
                        <p style={{ fontSize: '16px', marginBottom: '20px' }}>{popupMessage}</p>
                        <Button onClick={() => setShowPopup(false)} style={{ marginRight: '10px' }}>No</Button>
                        <Button onClick={() => window.open('/ContactSalesForm', '_blank')}>Yes</Button>
                    </div>
                )}
            </div>
        ) : null
    );
}

export { Map };